import { StyleSheet, css } from 'aphrodite'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

interface TimePickerProps {
  value: string | null
  onChange: (value: string) => void
  label: string
  buttonText?: string
  disabled?: boolean
  inModal?: boolean // New prop to indicate if used within a modal
  labelStyle?: object // New prop to allow custom label styling
  defaultTime?: string // Optional default time (e.g., "8:00 AM") used when value is null
}

const TimePicker: FC<TimePickerProps> = ({
  value,
  onChange,
  label,
  buttonText = 'Set Time',
  disabled = false,
  inModal = false, // Default to false for backward compatibility
  labelStyle,
  defaultTime,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedHour, setSelectedHour] = useState<number>(
    value
      ? Number.parseInt(value.split(':')[0]) || 12
      : defaultTime
        ? Number.parseInt(defaultTime.split(':')[0]) || 12
        : 12,
  )
  const [selectedMinute, setSelectedMinute] = useState<number>(
    value
      ? Number.parseInt(value.split(':')[1]?.split(' ')[0]) || 0
      : defaultTime
        ? Number.parseInt(defaultTime.split(':')[1]?.split(' ')[0]) || 0
        : 0,
  )
  const [selectedPeriod, setSelectedPeriod] = useState<'AM' | 'PM'>(
    value
      ? value.includes('PM')
        ? 'PM'
        : 'AM'
      : defaultTime
        ? defaultTime.includes('PM')
          ? 'PM'
          : 'AM'
        : 'AM',
  )

  useEffect(() => {
    if (value) {
      setSelectedHour(Number.parseInt(value.split(':')[0]) || 12)
      setSelectedMinute(
        Number.parseInt(value.split(':')[1]?.split(' ')[0]) || 0,
      )
      setSelectedPeriod(value.includes('PM') ? 'PM' : 'AM')
    } else if (defaultTime) {
      setSelectedHour(Number.parseInt(defaultTime.split(':')[0]) || 12)
      setSelectedMinute(
        Number.parseInt(defaultTime.split(':')[1]?.split(' ')[0]) || 0,
      )
      setSelectedPeriod(defaultTime.includes('PM') ? 'PM' : 'AM')
    }
  }, [value, defaultTime])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const hourElement = document.getElementById(`hour-${selectedHour}`)
        const minuteElement = document.getElementById(
          `minute-${selectedMinute}`,
        )

        hourElement?.scrollIntoView({
          block: 'center',
          inline: 'nearest',
        })
        minuteElement?.scrollIntoView({
          block: 'center',
          inline: 'nearest',
        })
      }, 100)
    }
  }, [isOpen, selectedHour, selectedMinute])

  // Handle clicking outside to close when in modal mode
  useEffect(() => {
    if (isOpen && inModal) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element
        if (!target.closest('.time-picker-container')) {
          setIsOpen(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, inModal])

  const hours = Array.from({ length: 12 }, (_, i) => i + 1)
  const minutes = Array.from({ length: 60 }, (_, i) => i)

  const handleTimeSelect = (
    hour: number,
    minute: number,
    period: 'AM' | 'PM',
  ) => {
    const formattedHour = hour
    const formattedMinute = minute.toString().padStart(2, '0')
    const timeString = `${formattedHour}:${formattedMinute} ${period}`
    onChange(timeString)
  }

  const handleSetCurrentTime = () => {
    if (!disabled) {
      const now = new Date()
      let hours = now.getHours()
      const minutes = now.getMinutes()
      const period = hours >= 12 ? 'PM' : 'AM'
      hours = hours % 12 || 12
      handleTimeSelect(hours, minutes, period)
    }
  }

  return (
    <div className={`${css(styles.container)} time-picker-container`}>
      <label className={css(labelStyle || styles.label)}>{label}</label>
      {value ? (
        <button
          type="button"
          className={css(styles.timeButton, disabled && styles.disabled)}
          onClick={() => !disabled && setIsOpen(true)}
          onKeyDown={(e) => e.key === 'Enter' && !disabled && setIsOpen(true)}
          disabled={disabled}
        >
          {value}
        </button>
      ) : (
        <button
          type="button"
          className={css(styles.loadButton, disabled && styles.disabled)}
          onClick={() => !disabled && setIsOpen(true)}
          onKeyDown={(e) => e.key === 'Enter' && !disabled && setIsOpen(true)}
          disabled={disabled}
        >
          <i className="fa-solid fa-plus" />
          Set Time
        </button>
      )}

      {isOpen && (
        <div className={css(inModal ? styles.modalInModal : styles.modal)}>
          <div className={css(styles.modalContent)}>
            <div className={css(styles.timeSelectors)}>
              <div className={css(styles.column)}>
                <div className={css(styles.columnLabel)}>Hour</div>
                <div className={css(styles.scrollContainer)}>
                  {hours.map((hour) => (
                    <button
                      key={hour}
                      id={`hour-${hour}`}
                      className={css(
                        styles.timeOption,
                        hour === selectedHour && styles.selected,
                      )}
                      onClick={() => {
                        setSelectedHour(hour)
                        handleTimeSelect(hour, selectedMinute, selectedPeriod)
                      }}
                    >
                      {hour}
                    </button>
                  ))}
                </div>
              </div>
              <div className={css(styles.column)}>
                <div className={css(styles.columnLabel)}>Minute</div>
                <div className={css(styles.scrollContainer)}>
                  {minutes.map((minute) => (
                    <button
                      key={minute}
                      id={`minute-${minute}`}
                      className={css(
                        styles.timeOption,
                        minute === selectedMinute && styles.selected,
                      )}
                      onClick={() => {
                        setSelectedMinute(minute)
                        handleTimeSelect(selectedHour, minute, selectedPeriod)
                      }}
                    >
                      {minute.toString().padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </div>
              <div className={css(styles.column)}>
                <div className={css(styles.columnLabel)}>Period</div>
                <div className={css(styles.periodContainer)}>
                  {['AM', 'PM'].map((period) => (
                    <button
                      key={period}
                      className={css(
                        styles.timeOption,
                        period === selectedPeriod && styles.selected,
                      )}
                      onClick={() => {
                        setSelectedPeriod(period as 'AM' | 'PM')
                        handleTimeSelect(
                          selectedHour,
                          selectedMinute,
                          period as 'AM' | 'PM',
                        )
                      }}
                    >
                      {period}
                    </button>
                  ))}
                  <button
                    className={css(styles.nowButton)}
                    onClick={handleSetCurrentTime}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && handleSetCurrentTime()
                    }
                  >
                    Now
                  </button>
                </div>
              </div>
            </div>
            <div className={css(styles.actionsRow)}>
              <button
                className={css(styles.doneButton)}
                onClick={() => {
                  if (!disabled) {
                    handleTimeSelect(
                      selectedHour,
                      selectedMinute,
                      selectedPeriod,
                    )
                    setIsOpen(false)
                  }
                }}
                onKeyDown={(e) => {
                  if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                    handleTimeSelect(
                      selectedHour,
                      selectedMinute,
                      selectedPeriod,
                    )
                    setIsOpen(false)
                  }
                }}
              >
                Done
              </button>
            </div>
          </div>
          {!inModal && (
            <div
              className={css(styles.modalOverlay)}
              onClick={() => setIsOpen(false)}
              onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
              role="button"
              tabIndex={0}
            />
          )}
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: '#111928',
    lineHeight: '150%',
    fontFamily: 'Inter',
  },
  timeButton: {
    padding: '8px 12px',
    background: '#F3F4F6',
    border: '1px solid rgb(209, 213, 219)',
    borderRadius: '8px',
    fontSize: '14px',
    width: '100%',
    marginTop: 8,
    fontFamily: 'Inter',
    textAlign: 'left',
    cursor: 'pointer',
    ':hover': {
      borderColor: '#1C64F2',
    },
  },
  loadButton: {
    padding: '8px 12px',
    border: '1px solid rgb(209, 213, 219)',
    borderRadius: '8px',
    width: '100%',
    marginTop: 8,
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    color: '#111928',
    fontFamily: 'Inter',
    ':hover': {
      background: '#F9FAFB',
    },
  },
  disabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    backgroundColor: '#E5E7EB',
    ':hover': {
      borderColor: '#E5E7EB',
      background: '#E5E7EB',
    },
  },
  modal: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,

    '@media only screen and (min-width: 1024px)': {
      marginLeft: 250,
    },
  },
  modalInModal: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    maxHeight: '300px',
    marginTop: '4px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
  },
  modalContent: {
    position: 'relative',
    backgroundColor: 'white',
    padding: '20px',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    zIndex: 1000,
    maxHeight: '80vh',
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '12px',
  },
  timeSelectors: {
    display: 'flex',
    gap: '20px',
    justifyContent: 'space-between',
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  columnLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    marginBottom: '8px',
  },
  scrollContainer: {
    height: '200px',
    overflowY: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  periodContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  timeOption: {
    width: '100%',
    padding: '12px',
    border: 'none',
    background: 'none',
    fontSize: '16px',
    color: '#111928',
    cursor: 'pointer',
    borderRadius: '8px',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  selected: {
    backgroundColor: '#EBF5FF',
    color: '#1C64F2',
    fontWeight: 600,
  },
  nowButton: {
    width: '100%',
    padding: '12px',
    border: 'none',
    background: 'none',
    fontSize: '16px',
    color: '#1C64F2',
    cursor: 'pointer',
    borderRadius: '8px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  doneButton: {
    padding: '10px 16px',
    backgroundColor: '#1C64F2',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#1D4ED8',
    },
  },
})

export default TimePicker
